import { createElement, useState } from "react";
import {
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  BookOpenIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { AdminLayout } from "../../../layouts/admin-layout";

type Status = "available" | "partial" | "needs-build" | "reference";

interface Skill {
  id: string;
  name: string;
  area: string;
  status: Status;
  description: string | null;
  playbook: boolean;
  availability: string[];
  updatedAt: string | null;
}

interface Group {
  area: string;
  total: number;
  available: number;
  partial: number;
  needsBuild: number;
  availableRatio: number;
  thin: boolean;
  skills: Skill[];
}

interface Summary {
  total: number;
  available: number;
  partial: number;
  needsBuild: number;
  reference: number;
  playbooks: number;
}

interface SkillsIndexProps {
  available: boolean;
  skillsRoot: string;
  summary: Summary;
  areas: Group[];
  thinAreas: string[];
}

const STATUS_META: Record<Status, { label: string; className: string }> = {
  "available": { label: "Available", className: "bg-green-100 text-green-800" },
  "partial": { label: "Partial", className: "bg-amber-100 text-amber-800" },
  "needs-build": { label: "Needs build", className: "bg-red-100 text-red-800" },
  "reference": { label: "Reference", className: "bg-slate-100 text-slate-600" },
};

function StatusBadge({ status }: { status: Status }) {
  const meta = STATUS_META[status] ?? STATUS_META.reference;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  accent: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        {createElement(icon, { className: "w-5 h-5" })}
      </div>
      <div>
        <div className="text-2xl font-bold text-foreground leading-none">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

/** Tiny stacked bar: green / amber / red proportions for a group. */
function HealthBar({ group }: { group: Group }) {
  const segs = [
    { n: group.available, c: "bg-green-500" },
    { n: group.partial, c: "bg-amber-400" },
    { n: group.needsBuild, c: "bg-red-500" },
  ].filter((s) => s.n > 0);
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
      {segs.map((s, i) => (
        <div key={i} className={s.c} style={{ width: `${(s.n / group.total) * 100}%` }} />
      ))}
    </div>
  );
}

function AreaRow({ group }: { group: Group }) {
  const [open, setOpen] = useState(group.thin);
  return (
    <div className={`rounded-xl border bg-card ${group.thin ? "border-red-300" : "border-border"}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-4 p-4 text-left"
      >
        {open ? (
          <ChevronDownIcon className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
        <div className="w-40 shrink-0">
          <div className="font-semibold text-foreground capitalize flex items-center gap-2">
            {group.area}
            {group.thin && (
              <ExclamationTriangleIcon className="w-4 h-4 text-red-500" title="Thin area" />
            )}
          </div>
          <div className="text-xs text-muted-foreground">{group.total} skills</div>
        </div>
        <div className="flex-1">
          <HealthBar group={group} />
        </div>
        <div className="flex gap-3 text-xs shrink-0 tabular-nums">
          <span className="text-green-700">{group.available} avail</span>
          <span className="text-amber-700">{group.partial} partial</span>
          <span className="text-red-700 font-medium">{group.needsBuild} needs build</span>
        </div>
      </button>

      {open && (
        <div className="border-t border-border divide-y divide-border">
          {group.skills.map((s) => (
            <div key={s.id} className="flex items-start gap-3 px-4 py-2.5 pl-12">
              <div className="w-56 shrink-0">
                <div className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  {s.name}
                  {s.playbook && (
                    <span className="text-[10px] uppercase tracking-wide text-purple-600 bg-purple-100 px-1 rounded">
                      playbook
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground font-mono">{s.id}</div>
              </div>
              <div className="flex-1 text-sm text-muted-foreground">{s.description}</div>
              <StatusBadge status={s.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SkillsIndex({ available, skillsRoot, summary, areas, thinAreas }: SkillsIndexProps) {
  if (!available) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground">Skills Audit</h1>
        <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-6 text-amber-900">
          <p className="font-medium">Skill registry not found on disk.</p>
          <p className="text-sm mt-1">
            Expected at <code className="font-mono">{skillsRoot}</code>. Set the{" "}
            <code className="font-mono">SKILLS_DIR</code> env var if the langgraph app lives elsewhere.
          </p>
        </div>
      </div>
    );
  }

  const buildable = summary.available + summary.partial + summary.needsBuild;
  const readyPct = buildable ? Math.round((summary.available / buildable) * 100) : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <SparklesIcon className="w-7 h-7" />
          Skills Audit
        </h1>
        <p className="text-muted-foreground mt-1">
          {summary.total} skills in the registry · {readyPct}% of buildable skills are fully available ·
          source <code className="font-mono text-xs">{skillsRoot}</code>
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <SummaryCard icon={CheckCircleIcon} label="Available" value={summary.available} accent="bg-green-100 text-green-700" />
        <SummaryCard icon={ExclamationTriangleIcon} label="Partial" value={summary.partial} accent="bg-amber-100 text-amber-700" />
        <SummaryCard icon={WrenchScrewdriverIcon} label="Needs build" value={summary.needsBuild} accent="bg-red-100 text-red-700" />
        <SummaryCard icon={SparklesIcon} label="Playbooks" value={summary.playbooks} accent="bg-purple-100 text-purple-700" />
        <SummaryCard icon={BookOpenIcon} label="Reference" value={summary.reference} accent="bg-slate-100 text-slate-600" />
      </div>

      {thinAreas.length > 0 && (
        <div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-800 font-medium">
            <ExclamationTriangleIcon className="w-5 h-5" />
            Thin areas need attention
          </div>
          <p className="text-sm text-red-700 mt-1">
            Fewer than 80% of shippable skills here are fully available:{" "}
            <span className="font-medium capitalize">{thinAreas.join(", ")}</span>
          </p>
        </div>
      )}

      <section className="mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-3">By area</h2>
        <div className="space-y-2">
          {areas.map((g) => (
            <AreaRow key={g.area} group={g} />
          ))}
        </div>
      </section>
    </div>
  );
}

SkillsIndex.layout = (page: React.ReactNode) => createElement(AdminLayout, null, page);

export default SkillsIndex;
